import { useState, useEffect, useMemo } from 'react';
import { contactRequestsService } from '../../services/api';

const useContactRequestsData = ({ isAuthenticated, isAdmin }) => {
  // State for API data
  const [requests, setRequests] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [countries, setCountries] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25
  });

  // Applied filters (used for API calls)
  const [serverFilters, setServerFilters] = useState({
    fromDate: '',
    toDate: '',
    country: '',
    profile: '',
    user: '',
    phone: '',
    postCode: '',
    recommendationId: '',
    comments: ''
  });

  // Form input values (separate from applied filters)
  const [filterInputs, setFilterInputs] = useState({
    fromDate: '',
    toDate: '',
    country: '',
    profile: '',
    user: '',
    phone: '',
    postCode: '',
    recommendationId: '',
    comments: ''
  });

  // Calculate page count
  const pageCount = useMemo(() => {
    return Math.ceil(totalCount / pagination.pageSize) || 0;
  }, [totalCount, pagination.pageSize]);

  // Helper function to format Date object to DD/MM/YYYY format for API
  const formatDateForAPI = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper function to parse date string into Date object
  const parseDateString = (dateString) => {
    if (!dateString) return null;
    try {
      // Parse DD/MM/YYYY format
      const parts = dateString.split('/');
      if (parts.length === 3) {
        return new Date(parts[2], parts[1] - 1, parts[0]); // Year, Month-1, Day
      }
      return null;
    } catch (err) {
      return null;
    }
  };


  // Fetch data with server-side parameters
  const fetchContactRequestsData = async () => {
    if (!isAuthenticated) {
      setRequests([]);
      setTotalCount(0);
      setCountries([]);
      setProfiles([]);
      setLoading(false);
      return;
    }


    try {
      setLoading(true);
      setError(null);

      const params = {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
        ...serverFilters
      };

      const data = await contactRequestsService.getContactRequestsData(params);

      if (data) {
        setRequests(data.requests || []);
        setTotalCount(data.totalCount || 0);
        setCountries(data.countries || []);
        // Profiles only returned for admin users
        if (isAdmin && data.profiles) {
          setProfiles(data.profiles || []);
        }
      } else {
        setRequests([]);
        setTotalCount(0);
        setCountries([]);
        setProfiles([]);
      }
    } catch (err) {
      console.error('Error fetching contact requests data:', err);

      // Extract user-friendly error message
      let errorMessage = 'Failed to load contact requests data. Please try again.';

      // Check HTTP status code first
      const status = err.response?.status;
      if (status === 404) {
        errorMessage = 'Service temporarily unavailable. Please try again later.';
      } else if (status === 401 || status === 403) {
        errorMessage = 'You are not authorized to view this data.';
      } else if (status === 500) {
        errorMessage = 'Server error occurred. Please try again later.';
      } else if (status >= 400) {
        errorMessage = 'Unable to connect to the server. Please try again later.';
      } else if (err.response?.data) {
        const responseData = err.response.data;

        // Check if response is HTML (error page)
        if (typeof responseData === 'string' && responseData.includes('<!doctype html')) {
          errorMessage = 'Service temporarily unavailable. Please try again later.';
        } else if (responseData.status === 'error' && responseData.message) {
          errorMessage = responseData.message;
        } else if (responseData.message) {
          errorMessage = responseData.message;
        }
      } else if (err.isTimeout) {
        errorMessage = 'Request timed out. Please check your connection and try again.';
      } else if (err.message && !err.message.includes('<!doctype')) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setRequests([]);
      setTotalCount(0);
      setCountries([]);
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when dependencies change
  useEffect(() => {
    fetchContactRequestsData();
  }, [isAuthenticated, isAdmin, pagination.pageIndex, pagination.pageSize, serverFilters]);

  // Handle input changes (doesn't trigger API call)
  const handleInputChange = (field, value) => {
    setFilterInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply filters (triggers API call)
  const applyFilters = () => {
    setServerFilters(filterInputs);
    // Reset to first page when filters change
    setPagination(prev => ({
      ...prev,
      pageIndex: 0
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    const emptyFilters = {
      fromDate: '',
      toDate: '',
      country: '',
      profile: '',
      user: '',
      phone: '',
      postCode: '',
      recommendationId: '',
      comments: ''
    };

    setServerFilters(emptyFilters);
    setFilterInputs(emptyFilters);
    // Reset to first page when filters are cleared
    setPagination(prev => ({
      ...prev,
      pageIndex: 0
    }));
  };

  return {
    // Data
    requests,
    totalCount,
    pageCount,
    countries,
    profiles,
    loading,
    error,

    // Pagination
    pagination,
    setPagination,

    // Filters
    serverFilters,
    filterInputs,
    handleInputChange,
    applyFilters,
    clearAllFilters,

    // Date helpers
    parseDateString,
    formatDateForAPI
  };
};

export default useContactRequestsData;
